import {
    ChangeEvent,
    InputHTMLAttributes,
    MutableRefObject,
    ReactNode,
    forwardRef,
    useId,
    useState,
} from "react";
import { cx } from "../../utils/cx";
import styles from "./Switch.module.scss";

export type SwitchVariant = "default" | "danger";

export interface SwitchProps
    extends Omit<
        InputHTMLAttributes<HTMLInputElement>,
        "size" | "type" | "role"
    > {
    /** Visible label to the right of the switch. */
    label?: ReactNode;
    showLabel?: boolean;
    error?: boolean;
    /** Checked fill style. `danger` uses error red + white thumb (same layout). */
    variant?: SwitchVariant;
    /**
     * Optional visual override for Storybook/Figma matrix snapshots.
     * Leaves native interaction intact when omitted.
     */
    visualState?: "enabled" | "hovered" | "pressed" | "disabled";
    onCheckedChange?: (checked: boolean) => void;
}

/**
 * Treez DS — Switch (Wave 1).
 *
 * Figma: `Switch` (`qgFv3OQga6RQ4luvSFuGFP`, node `7754:50650`).
 *
 * Uses a native checkbox with `role="switch"` for semantics and keyboard
 * (Space toggles). Visual track + thumb are decorative spans.
 */
export const Switch = forwardRef<HTMLInputElement, SwitchProps>(function Switch(
    {
        label = "Switch Label",
        showLabel = true,
        checked,
        defaultChecked,
        disabled = false,
        error = false,
        variant = "default",
        visualState,
        className,
        id,
        name,
        onChange,
        onCheckedChange,
        ...rest
    },
    ref,
) {
    const generatedId = useId();
    const inputId = id ?? `ds-switch-${generatedId}`;
    const isControlled = checked !== undefined;
    const [uncontrolledChecked, setUncontrolledChecked] = useState(
        Boolean(defaultChecked),
    );
    const currentChecked = isControlled
        ? Boolean(checked)
        : uncontrolledChecked;

    const setRefs = (node: HTMLInputElement | null) => {
        if (typeof ref === "function") {
            ref(node);
        } else if (ref) {
            (ref as MutableRefObject<HTMLInputElement | null>).current = node;
        }
    };

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        if (!isControlled) {
            setUncontrolledChecked(event.target.checked);
        }
        onChange?.(event);
        onCheckedChange?.(event.target.checked);
    };

    return (
        <label
            className={cx(styles.root, className)}
            data-checked={currentChecked || undefined}
            data-disabled={disabled || visualState === "disabled" || undefined}
            data-error={error || undefined}
            data-ui="switch"
            data-variant={variant !== "default" ? variant : undefined}
            data-visual-state={visualState || undefined}
            htmlFor={inputId}
        >
            <input
                checked={isControlled ? checked : undefined}
                className={styles.input}
                defaultChecked={isControlled ? undefined : defaultChecked}
                disabled={disabled || visualState === "disabled"}
                id={inputId}
                name={name}
                onChange={handleChange}
                ref={setRefs}
                role="switch"
                type="checkbox"
                {...rest}
            />
            <span aria-hidden className={styles.track}>
                <span className={styles.thumb} />
            </span>
            {showLabel && <span className={styles.label}>{label}</span>}
        </label>
    );
});

Switch.displayName = "Switch";
